import { toDigits } from '@imprint-payments/imprint-lib/dist/utilities/toDigits';
import { useMutation } from '@tanstack/react-query';

import { EVENT } from '../../analytics/analyticsConsts';
import {
  MODAL_ENUM,
  setLoading,
  setShowModal,
} from '../../app/applicationScreenSlice';
import { setConsumerProfile } from '../../app/consumerInfoSlice';
import { useAppDispatch, useImpState } from '../../app/hooks';
import {
  setCheckoutAppState,
  setLoginFlow,
  setNextStep,
} from '../../app/loginSlice';
import { SCREEN, setScreen } from '../../app/screenStateSlice';
import {
  PrefillStep,
  useProveContext,
} from '../../components/prove/ProveContext';
import { ANALYTICS } from '../../firebase/firebase';
import { showError } from '../api';
import { CHECKOUT_APPLICATION_STATE } from '../apiConstants';
import { checkout, CheckoutError, CheckoutResponse } from './checkout.api';
import useLogin from './useLogin';

export interface CheckoutParams {
  phone: string;
  ssn4: string;
}

function useCheckoutAndLogin() {
  const { merchantData, appScreenState, loginState, consumerProfile } =
    useImpState();
  const dispatch = useAppDispatch();
  const loginMutation = useLogin();
  const { setStep } = useProveContext();

  return useMutation<CheckoutResponse, CheckoutError, CheckoutParams>({
    mutationFn: (params) =>
      checkout({
        phone: params.phone,
        productUUID: merchantData.productUUID,
      }),
    onSettled: () => {
      dispatch(setLoading(false));
    },
    onSuccess: (checkoutData, checkoutParams) => {
      dispatch(
        setConsumerProfile({
          ...consumerProfile,
          phone: toDigits(checkoutParams.phone),
          ssn4: checkoutParams.ssn4,
        })
      );

      // if user is using an in store device AND already has the card, we prevent login
      if (
        appScreenState.inStoreDevice &&
        checkoutData.applicationState === CHECKOUT_APPLICATION_STATE.APPROVED
      ) {
        dispatch(setShowModal(MODAL_ENUM.showWelcomeBackInStoreModal));
        return;
      }

      loginMutation.mutate(
        { phone: checkoutParams.phone },
        {
          onSettled: () => {
            dispatch(setLoading(false));
          },
          onSuccess: (loginData) => {
            if (checkoutData.applicationState) {
              // Existing application
              dispatch(setCheckoutAppState(checkoutData.applicationState));
            }
            dispatch(setNextStep(loginData.nextStep));
            setLoginFlow({ ...loginState.loginFlow, loginError: false });
            setStep(PrefillStep.Verification);
            return;
          },
          onError: (err) => {
            if (err.code === '201') {
              dispatch(setScreen(SCREEN.ACCOUNT_LOCKED));
              return;
            }
            dispatch(
              setLoginFlow({ ...loginState.loginFlow, loginError: true })
            );
          },
        }
      );
    },
    onError: (err) => {
      ANALYTICS.logEvent(EVENT.ERROR_VIEWED, {
        error_type: err.message,
      });
      showError(dispatch);
    },
  });
}

export default useCheckoutAndLogin;
